import React, { DetailedHTMLProps } from "react";
import { IFieldProps } from "./IFieldProps";
import { Typography } from "@material-ui/core";
import { timespan } from "../../../common/util/timespan";
import { parseISO, isPast, differenceInMilliseconds } from "date-fns";
import { sanitizeFieldProps } from "../../../common/util/sanitizeFieldProps";
import {
  useLocale,
  useTranslate,
  Translate,
  TextFieldProps,
} from "react-admin";
import { isNumber } from "lodash";

type InputType = "hours" | "minutes" | "seconds" | "milliseconds" | "datetime";
type OutputType = "long" | "short";

const formatDateTime = (
  input: Date,
  outputType: OutputType,
  displayDays: boolean,
  props: Partial<
    DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
  >,
  locale: string,
  translate: Translate
) => {
  const now = new Date();
  const isInPast = isPast(input);
  const delta = differenceInMilliseconds(now, input);

  let pattern: string;
  switch (outputType) {
    case "long":
      if (delta > 24 * 60 * 60 * 1000 && displayDays) {
        pattern = `D~ h~ m~`;
        break;
      }

      pattern = `h~ m~`;
      break;

    case "short":
      if (delta > 24 * 60 * 60 * 1000 && displayDays) {
        pattern = `Dd h:m`;
        break;
      }

      pattern = `h:m`;
      break;
  }

  timespan.locale(locale);
  return (
    <div {...sanitizeFieldProps(props)}>
      <Typography variant="body2">
        {translate(isInPast ? "timespan.inPast" : "timespan.inFuture", {
          time: timespan.format(pattern, delta),
        })}
      </Typography>
    </div>
  );
};

const isDate = (input: any): input is Date => {
  return input instanceof Date;
};

const isString = (input: any): input is string => {
  return typeof input === "string";
};

const TimeField = (
  props: IFieldProps & {
    inputType: InputType;
    outputType?: OutputType;
    displayDays?: boolean;
    allowNegative?: boolean;
    displayEmptyValue?: boolean;
    emptyDisplay?: string;
  } & DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> &
    TextFieldProps
) => {
  let {
    source,
    record,
    inputType,
    outputType,
    displayDays,
    label,
    allowNegative,
    emptyDisplay,
    displayEmptyValue = true,
    ...rest
  } = props;

  const locale = useLocale();
  const translate = useTranslate();

  if (!record || !source) {
    return null;
  }

  if (!record[source] && record[source] !== 0) {
    return (
      <div {...sanitizeFieldProps(rest)}>
        <Typography variant="body2">{emptyDisplay ?? null}</Typography>
      </div>
    );
  }

  let input = record[source] as number | string | Date;
  if (typeof input !== "number" && inputType !== "datetime") {
    return null;
  }

  if (!allowNegative && input < 0) {
    input = 0;
  }

  outputType = outputType ?? "long";
  displayDays = displayDays ?? false;

  switch (inputType) {
    case "milliseconds":
      break;

    case "seconds":
      if (typeof input === "number") {
        input *= 1000;
      } else {
        throw new Error(
          "The input type is seconds but the input is not a number"
        );
      }

      break;

    case "minutes":
      if (typeof input === "number") {
        input *= 60 * 1000;
      } else {
        throw new Error(
          "The input type is seconds but the input is not a number"
        );
      }

      break;

    case "hours":
      if (typeof input === "number") {
        input *= 60 * 60 * 1000;
      } else {
        throw new Error(
          "The input type is seconds but the input is not a number"
        );
      }
      break;

    case "datetime":
      if (isDate(input)) {
        return formatDateTime(
          input,
          outputType,
          displayDays,
          rest,
          locale,
          translate
        );
      } else if (isNumber(input)) {
        return formatDateTime(
          new Date(input),
          outputType,
          displayDays,
          rest,
          locale,
          translate
        );
      } else if (isString(input)) {
        let date: Date;
        try {
          date = parseISO(input);
        } catch (err) {
          throw new Error(
            `The input type is datetime, but the input '${input}' is not a valid date: ${err}`
          );
        }

        return formatDateTime(
          date,
          outputType,
          displayDays,
          rest,
          locale,
          translate
        );
      } else {
        throw new Error(
          `The input type is datetime, but the input is neither Date or string, but ${typeof input}`
        );
      }
  }

  let pattern: string;
  switch (outputType) {
    case "long":
      if (input > 24 * 60 * 60 * 1000 && displayDays) {
        pattern = "D~ h~ m~";
        break;
      }

      pattern = "h~ m~";
      break;

    case "short":
      if (input > 24 * 60 * 60 * 1000 && displayDays) {
        pattern = "Dd h:m";
        break;
      }

      pattern = "h:m";
      break;
  }

  timespan.locale(locale);
  return (
    <div {...sanitizeFieldProps(rest)}>
      <Typography variant="body2">
        {input
          ? timespan.format(pattern, input)
          : displayEmptyValue
          ? emptyDisplay ?? timespan.format(pattern, input)
          : emptyDisplay ?? null}
      </Typography>
    </div>
  );
};

export default TimeField;
